// File: src/pages/customers/CustomerListCompact.jsx
// Requirements satisfied:
// - Do NOT use the old page UI / SCSS / AgGridReact
// - Inline CSS only
// - SuperAdmin import roots -> ../import
// - CheckPermission respected
// - Column chooser added
// - PDF download removed
// - Excel download follows the uploaded CustomerList template format
// - Bulk delete (only when Delete perm is true)
// - View / Edit / Delete actions keep your existing routes

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Row, Col, Card, Input, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaFileExcel, FaColumns } from "react-icons/fa";
import swal from "sweetalert";

// ---- your shared helpers for SuperAdmin pages ----
import { CheckPermission } from "../import/CheckPermission";
import {
  CreateCustomerList,
  DeleteCustomerList,
} from "../import/ApiEndPoint/ApiCalling";
const BulkRegionFixButton = React.lazy(
  () => import("../components/BulkRegionFixButton"),
);

const COLUMN_STORAGE_KEY = "customerListVisibleColumns";
const PAGE_SIZE = 100;

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmtDate = (iso) => (iso ? String(iso).split("T")[0] : "");

const getObjectLabel = (value) => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => getObjectLabel(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    return toStr(
      value?.name ??
        value?.title ??
        value?.label ??
        value?.CompanyName ??
        value?.groupName ??
        value?.transporterName ??
        value?.serviceArea ??
        value?.id ??
        value?._id ??
        "",
    );
  }

  return toStr(value);
};

const getCategoryName = (customer) =>
  getObjectLabel(customer?.category?.groupName ?? customer?.category);

const getOpeningBalance = (customer) =>
  customer?.OpeningBalance ?? customer?.openingBalance ?? "";

const getCustomerType = (customer) => customer?.Type ?? customer?.type ?? "";

const getOpeningFinancialYear = (customer) =>
  customer?.openingFinancialYear ?? customer?.OpeningFinancialYear ?? "";

const CUSTOMER_COLUMNS = [
  {
    key: "id",
    label: "ID",
    value: (c) => toStr(c?.id),
  },
  {
    key: "CompanyName",
    label: "COMPANY",
    value: (c) => toStr(c?.CompanyName),
    align: "left",
    companyLink: true,
  },
  {
    key: "ownerName",
    label: "OWNER",
    value: (c) => toStr(c?.ownerName),
    align: "left",
  },
  {
    key: "contactNumber",
    label: "CONTACT",
    value: (c) => toStr(c?.contactNumber),
  },
  {
    key: "mobileNumber",
    label: "MOBILE",
    value: (c) => toStr(c?.mobileNumber),
  },
  {
    key: "email",
    label: "EMAIL",
    value: (c) => toStr(c?.email),
    align: "left",
  },
  {
    key: "State",
    label: "STATE",
    value: (c) => toStr(c?.State),
  },
  {
    key: "City",
    label: "CITY",
    value: (c) => toStr(c?.City),
  },
  {
    key: "pincode",
    label: "PINCODE",
    value: (c) => toStr(c?.pincode),
  },
  {
    key: "regionType",
    label: "REGION TYPE",
    value: (c) => toStr(c?.regionType),
  },
  {
    key: "status",
    label: "STATUS",
    value: (c) => toStr(c?.status),
  },
  {
    key: "createdAt",
    label: "CREATED",
    value: (c) => fmtDate(c?.createdAt),
  },
  {
    key: "updatedAt",
    label: "UPDATED",
    value: (c) => fmtDate(c?.updatedAt),
  },
  {
    key: "gstNumber",
    label: "GST NUMBER",
    value: (c) => toStr(c?.gstNumber),
  },
  {
    key: "comPanNo",
    label: "COMPANY PAN",
    value: (c) => toStr(c?.comPanNo),
  },
  {
    key: "category",
    label: "CATEGORY",
    value: (c) => getCategoryName(c),
  },
  {
    key: "partyType",
    label: "PARTY TYPE",
    value: (c) => toStr(c?.partyType),
  },
  {
    key: "limit",
    label: "LIMIT",
    value: (c) => toStr(c?.limit),
  },
  {
    key: "lockInTime",
    label: "LOCK-IN TIME",
    value: (c) => toStr(c?.lockInTime),
  },
  {
    key: "paymentTerm",
    label: "PAYMENT TERM",
    value: (c) => toStr(c?.paymentTerm),
  },
  {
    key: "registrationType",
    label: "REGISTRATION TYPE",
    value: (c) => toStr(c?.registrationType),
  },
  {
    key: "aadharNo",
    label: "AADHAR NO",
    value: (c) => toStr(c?.aadharNo),
  },
  {
    key: "transporterDetail",
    label: "TRANSPORTER",
    value: (c) => getObjectLabel(c?.transporterDetail),
  },
  {
    key: "serviceArea",
    label: "SERVICE AREA",
    value: (c) => getObjectLabel(c?.serviceArea),
  },
  {
    key: "duedate",
    label: "DUE DATE",
    value: (c) => fmtDate(c?.duedate ?? c?.dueDate),
  },
  {
    key: "shopSize",
    label: "SHOP SIZE",
    value: (c) => toStr(c?.shopSize),
  },
  {
    key: "dealsInProducts",
    label: "DEALS IN PRODUCTS",
    value: (c) => getObjectLabel(c?.dealsInProducts),
  },
  {
    key: "annualTurnover",
    label: "ANNUAL TURNOVER",
    value: (c) => toStr(c?.annualTurnover),
  },
  {
    key: "OpeningBalance",
    label: "OPENING BALANCE",
    value: (c) => toStr(getOpeningBalance(c)),
  },
  {
    key: "Type",
    label: "TYPE",
    value: (c) => toStr(getCustomerType(c)),
  },
  {
    key: "openingFinancialYear",
    label: "OPENING FINANCIAL YEAR",
    value: (c) => toStr(getOpeningFinancialYear(c)),
  },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "id",
  "CompanyName",
  "ownerName",
  "contactNumber",
  "mobileNumber",
  "email",
  "State",
  "City",
  "pincode",
  "regionType",
  "status",
  "createdAt",
  "updatedAt",
];

// Exact header order from the uploaded CustomerList Excel file.
const EXCEL_HEADERS = [
  "CompanyName",
  "gstNumber",
  "contactNumber",
  "partyType",
  "limit",
  "lockInTime",
  "paymentTerm",
  "State",
  "City",
  "pincode",
  "registrationType",
  "comPanNo",
  "mobileNumber",
  "aadharNo",
  "transporterDetail",
  "serviceArea",
  "duedate",
  "shopSize",
  "email",
  "dealsInProducts",
  "annualTurnover",
  "ownerName",
  "OpeningBalance",
  "Type",
  "openingFinancialYear",
];

const EXCEL_COLUMN_WIDTHS = [
  34, 20, 18, 14, 14, 14, 16, 22, 24, 12, 18, 18, 18, 20, 22, 22, 14, 16, 32,
  22, 18, 24, 18, 14, 22,
];

const injectOnce = () => {
  if (document.getElementById("cust-list-inline-css")) return;
  const s = document.createElement("style");
  s.id = "cust-list-inline-css";
  s.textContent = `
    .cu-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .cu-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .cu-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .cu-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px; }
    .cu-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap;
    }
    .cu-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center;
      white-space:nowrap;
    }
    .cu-table tbody tr:hover{ background:#f9fbff; cursor:pointer; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
    .chip-eye{ background:#39cccc; }
    .cu-exp-row{ background:#fcfcff; }
    .cu-exp-box{ text-align:left; padding:10px 12px; }
    .cu-exp-title{ font-weight:700; margin-bottom:6px; color:#111827; }
    .cu-exp-grid{ display:grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap:8px; }
    .cu-exp-item{ border:1px solid #e5e7eb; border-radius:10px; padding:8px; background:white; }
    .cu-exp-h{ font-size:12px; color:#6b7280; margin-bottom:4px; text-transform:uppercase; letter-spacing:.03em;}
    .cu-exp-v{ font-size:13px; color:#111827; }
    .selbox{ display:flex; align-items:center; justify-content:center; }

    .cu-dl-btn,
    .cu-column-btn{
      height:32px;
      border-radius:10px;
      border:1px solid #e5e7eb;
      background:#ffffff;
      color:#0f172a;
      font-weight:800;
      text-transform:uppercase;
      padding:0 10px;
      display:inline-flex;
      align-items:center;
      gap:8px;
      box-shadow:0 8px 16px rgba(0,0,0,0.04);
      cursor:pointer;
    }
    .cu-dl-btn:hover,
    .cu-column-btn:hover{
      box-shadow:0 12px 22px rgba(0,0,0,0.08);
      transform:translateY(-1px);
    }

    .cu-column-wrap{ position:relative; }
    .cu-column-menu{
      position:absolute;
      right:0;
      top:38px;
      width:285px;
      max-height:430px;
      overflow:hidden;
      background:#ffffff;
      border:1px solid #dbe2ea;
      border-radius:12px;
      box-shadow:0 18px 42px rgba(15,23,42,0.16);
      z-index:1000;
    }
    .cu-column-menu-head{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:8px;
      padding:10px;
      border-bottom:1px solid #e5e7eb;
      background:#f8fafc;
    }
    .cu-column-title{ font-size:13px; font-weight:800; color:#0f172a; }
    .cu-column-actions{ display:flex; align-items:center; gap:6px; }
    .cu-column-action{
      border:0;
      background:transparent;
      color:#2563eb;
      padding:2px 4px;
      font-size:11px;
      font-weight:700;
      cursor:pointer;
    }
    .cu-column-options{ max-height:360px; overflow:auto; padding:6px; }
    .cu-column-option{
      display:flex;
      align-items:center;
      gap:9px;
      padding:8px;
      border-radius:8px;
      font-size:13px;
      color:#0f172a;
      cursor:pointer;
      user-select:none;
    }
    .cu-column-option:hover{ background:#f1f5f9; }
    .cu-column-option input{ margin:0; }

    @media (max-width: 900px){
      .cu-exp-grid{ grid-template-columns: repeat(2, minmax(150px, 1fr)); }
      .cu-column-menu{ right:auto; left:0; }
    }
  `;
  document.head.appendChild(s);
};

export default function CustomerListCompact() {
  const navigate = useNavigate();
  const columnChooserRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [columnChooserOpen, setColumnChooserOpen] = useState(false);

  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(COLUMN_STORAGE_KEY) || "null",
      );
      const validKeys = new Set(CUSTOMER_COLUMNS.map((column) => column.key));
      const validSaved = Array.isArray(saved)
        ? saved.filter((key) => validKeys.has(key))
        : [];

      return validSaved.length ? validSaved : DEFAULT_VISIBLE_COLUMNS;
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set()); // bulk delete

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumnKeys));
  }, [visibleColumnKeys]);

  useEffect(() => {
    const closeChooser = (event) => {
      if (
        columnChooserRef.current &&
        !columnChooserRef.current.contains(event.target)
      ) {
        setColumnChooserOpen(false);
      }
    };

    document.addEventListener("mousedown", closeChooser);
    return () => document.removeEventListener("mousedown", closeChooser);
  }, []);

  const visibleColumns = useMemo(() => {
    const selectedKeys = new Set(visibleColumnKeys);
    return CUSTOMER_COLUMNS.filter((column) => selectedKeys.has(column.key));
  }, [visibleColumnKeys]);

  const reload = useCallback(async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = userinfo?._id || userinfo?.id;
    const db = userinfo?.database || userinfo?.db || "";

    // Your existing list function signature: CreateCustomerList(userId, db)
    const res = await CreateCustomerList(userId, db);
    const list = Array.isArray(res?.Customer) ? res.Customer : [];
    setRows([...list].reverse());
  }, []);

  useEffect(() => {
    const inside = CheckPermission("Create Customer");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        await reload();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reload]);

  // Build searchable text once per API load instead of rebuilding it on every keypress.
  const searchableRows = useMemo(
    () =>
      rows.map((row) => ({
        row,
        searchText: [
          row?.CompanyName,
          row?.ownerName,
          row?.email,
          row?.contactNumber,
          row?.mobileNumber,
          row?.pincode,
          row?.City,
          row?.State,
          row?.status,
          row?.id,
          row?.gstNumber,
          row?.comPanNo,
          row?.partyType,
          row?.registrationType,
          row?.paymentTerm,
          row?.regionType,
          getCategoryName(row),
          getObjectLabel(row?.transporterDetail),
          getObjectLabel(row?.serviceArea),
          getObjectLabel(row?.dealsInProducts),
          fmtDate(row?.createdAt),
          fmtDate(row?.updatedAt),
        ]
          .map(toStr)
          .join(" ")
          .toLowerCase(),
      })),
    [rows],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return searchableRows
      .filter((item) => item.searchText.includes(term))
      .map((item) => item.row);
  }, [rows, search, searchableRows]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage],
  );

  useEffect(() => setPage(1), [search]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleColumn = (key) => {
    setVisibleColumnKeys((prev) => {
      if (prev.includes(key)) {
        // Keep at least one data column visible.
        if (prev.length === 1) return prev;
        return prev.filter((columnKey) => columnKey !== key);
      }

      return [...prev, key];
    });
  };

  const selectAllColumns = () => {
    setVisibleColumnKeys(CUSTOMER_COLUMNS.map((column) => column.key));
  };

  const resetColumns = () => {
    setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMNS);
  };

  const handleDelete = async (id) => {
    if (!permissions?.Delete) return;
    const choice = await swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    });
    if (choice === "delete") {
      try {
        await DeleteCustomerList(id);
        setRows((prev) => prev.filter((x) => x?._id !== id));
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      } catch {
        swal("Error", "Could not delete customer", "error");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!permissions?.Delete || selected.size === 0) return;
    const choice = await swal("Warning", "Delete selected customers?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    });
    if (choice !== "delete") return;

    try {
      const ids = Array.from(selected);
      const deletedIds = new Set();

      // Delete in small parallel batches: much faster than one-by-one without flooding the API.
      for (let i = 0; i < ids.length; i += 10) {
        const batch = ids.slice(i, i + 10);
        const results = await Promise.allSettled(
          batch.map((id) => DeleteCustomerList(id)),
        );

        results.forEach((result, index) => {
          if (result.status === "fulfilled") deletedIds.add(batch[index]);
        });
      }

      setRows((prev) => prev.filter((x) => !deletedIds.has(x?._id)));
      setSelected(new Set());

      if (deletedIds.size === ids.length) {
        swal("Done", "Selected customers deleted", "success");
      } else {
        swal(
          "Partially Completed",
          `${deletedIds.size} of ${ids.length} customers deleted`,
          "warning",
        );
      }
    } catch {
      swal("Error", "Bulk delete failed", "error");
    }
  };

  /* ---------------- Excel export ---------------- */
  const buildExcelRows = () => {
    return filtered.map((c) => [
      toStr(c?.CompanyName),
      toStr(c?.gstNumber),
      toStr(c?.contactNumber),
      toStr(c?.partyType),
      c?.limit ?? "",
      c?.lockInTime ?? "",
      toStr(c?.paymentTerm),
      toStr(c?.State),
      toStr(c?.City),
      toStr(c?.pincode),
      toStr(c?.registrationType),
      toStr(c?.comPanNo),
      toStr(c?.mobileNumber),
      toStr(c?.aadharNo),
      getObjectLabel(c?.transporterDetail),
      getObjectLabel(c?.serviceArea),
      c?.duedate ?? c?.dueDate ?? "",
      toStr(c?.shopSize),
      toStr(c?.email),
      getObjectLabel(c?.dealsInProducts),
      c?.annualTurnover ?? "",
      toStr(c?.ownerName),
      getOpeningBalance(c),
      toStr(getCustomerType(c)),
      toStr(getOpeningFinancialYear(c)),
    ]);
  };

  const downloadXLSX = async () => {
    const XLSXModule = await import("xlsx");
    const XLSX = XLSXModule.default || XLSXModule;

    const ws = XLSX.utils.aoa_to_sheet([EXCEL_HEADERS, ...buildExcelRows()]);

    // Keep the same header order as the uploaded file and make all columns readable.
    ws["!cols"] = EXCEL_COLUMN_WIDTHS.map((wch) => ({ wch }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "CustomerList.xlsx");
  };

  const tableColumnCount = 2 + visibleColumns.length;

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="cu-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="cu-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Customer List{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 260px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  className="input-color"
                  placeholder="Search customers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Delete && selected.size > 0 && (
                <Button
                  color="danger"
                  onClick={handleBulkDelete}
                  style={{ height: 32 }}
                >
                  Delete Selected ({selected.size})
                </Button>
              )}

              {/* Column chooser */}
              <div className="cu-column-wrap" ref={columnChooserRef}>
                <button
                  type="button"
                  className="cu-column-btn"
                  onClick={() => setColumnChooserOpen((prev) => !prev)}
                  title="Choose visible columns"
                >
                  <FaColumns size={15} />
                  COLUMNS
                </button>

                {columnChooserOpen && (
                  <div className="cu-column-menu">
                    <div className="cu-column-menu-head">
                      <div className="cu-column-title">
                        Select Columns ({visibleColumns.length}/
                        {CUSTOMER_COLUMNS.length})
                      </div>
                      <div className="cu-column-actions">
                        <button
                          type="button"
                          className="cu-column-action"
                          onClick={selectAllColumns}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="cu-column-action"
                          onClick={resetColumns}
                        >
                          Default
                        </button>
                      </div>
                    </div>

                    <div className="cu-column-options">
                      {CUSTOMER_COLUMNS.map((column) => (
                        <label key={column.key} className="cu-column-option">
                          <input
                            type="checkbox"
                            checked={visibleColumnKeys.includes(column.key)}
                            onChange={() => toggleColumn(column.key)}
                          />
                          <span>{column.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {permissions?.Download && (
                <button
                  type="button"
                  className="cu-dl-btn"
                  onClick={downloadXLSX}
                  title="Download Excel"
                >
                  <FaFileExcel size={16} />
                  EXCEL
                </button>
              )}

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    textTransform: "uppercase",
                  }}
                  onClick={() => navigate(`/app/customers/create/0`)}
                >
                  <FaPlus size={12} /> Customer
                </Button>
              )}

              {permissions?.Edit && (
                <React.Suspense fallback={null}>
                  <BulkRegionFixButton onDone={reload} />
                </React.Suspense>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card className="cu-card" style={{ padding: 8 }}>
            {loading ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                <Spinner size="sm" color="primary" /> Loading customers...
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No customers found.
              </div>
            ) : (
              <div className="cu-table-wrap">
                <table
                  className="cu-table"
                  style={{
                    minWidth: Math.max(700, 130 + visibleColumns.length * 125),
                  }}
                >
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            const all = new Set(selected);
                            if (e.target.checked) {
                              filtered.forEach((r) => r?._id && all.add(r._id));
                            } else {
                              filtered.forEach(
                                (r) => r?._id && all.delete(r._id),
                              );
                            }
                            setSelected(all);
                          }}
                          checked={
                            filtered.length > 0 &&
                            filtered.every((r) => selected.has(r?._id))
                          }
                        />
                      </th>
                      <th>#</th>
                      {visibleColumns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pagedRows.map((c, i) => {
                      const isOpen = expanded.has(c?._id);
                      return (
                        <React.Fragment key={c?._id || i}>
                          <tr onClick={() => toggleExpand(c?._id)}>
                            <td
                              className="selbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selected.has(c?._id)}
                                onChange={(e) =>
                                  toggleSelect(c?._id, e.target.checked)
                                }
                              />
                            </td>

                            <td>{(safePage - 1) * PAGE_SIZE + i + 1}</td>

                            {visibleColumns.map((column) => {
                              const value = column.value(c);

                              return (
                                <td
                                  key={column.key}
                                  style={{
                                    textAlign: column.align || "center",
                                    color: column.companyLink
                                      ? "#2d5cf7"
                                      : undefined,
                                  }}
                                  onClick={
                                    column.companyLink
                                      ? (e) => {
                                          e.stopPropagation();
                                          navigate(
                                            `/app/customers/create/${c?._id}`,
                                          );
                                        }
                                      : undefined
                                  }
                                >
                                  {value}
                                </td>
                              );
                            })}
                          </tr>

                          {/* Expandable details */}
                          {isOpen && (
                            <tr className="cu-exp-row">
                              <td
                                colSpan={tableColumnCount}
                                className="cu-exp-box"
                              >
                                <div className="cu-exp-title">Details</div>
                                <div className="cu-exp-grid">
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">GST Number</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.gstNumber)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">Company PAN</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.comPanNo)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">Category</div>
                                    <div className="cu-exp-v">
                                      {getCategoryName(c)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">Payment Term</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.paymentTerm)}
                                    </div>
                                  </div>
                                  {/* <div className="cu-exp-item">
                                    <div className="cu-exp-h">Lock-in</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.lockInTime)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">
                                      Annual Turnover
                                    </div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.annualTurnover)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">Address 1</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.address1)}
                                    </div>
                                  </div>
                                  <div className="cu-exp-item">
                                    <div className="cu-exp-h">Address 2</div>
                                    <div className="cu-exp-v">
                                      {toStr(c?.address2)}
                                    </div>
                                  </div> */}

                                  {/* Optional quick delete inside expand (kept logic intact) */}
                                  {/* {permissions?.Delete && (
                                    <div className="cu-exp-item">
                                      <div className="cu-exp-h">
                                        Danger Zone
                                      </div>
                                      <div className="cu-exp-v">
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(c?._id);
                                          }}
                                        >
                                          Delete this customer
                                        </Button>
                                      </div>
                                    </div>
                                  )} */}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filtered.length > PAGE_SIZE && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  paddingTop: 12,
                }}
              >
                <Button
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <span style={{ fontSize: 13, fontWeight: 700 }}>
                  {(safePage - 1) * PAGE_SIZE + 1}-
                  {Math.min(safePage * PAGE_SIZE, filtered.length)} of{" "}
                  {filtered.length} · Page {safePage} of {pageCount}
                </span>
                <Button
                  size="sm"
                  disabled={safePage >= pageCount}
                  onClick={() =>
                    setPage((prev) => Math.min(pageCount, prev + 1))
                  }
                >
                  Next
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
